import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '../../../i18n/uiText';
import { jsonHeadersWithAuth } from '../../../utils/webAuth';
import { requestAuthPopup } from '../../../utils/authPopup';
import { resolveAiLimitMessage } from '../../../utils/aiLimitMessage';
import { telegramService } from '../../../services/telegram';
import { useTelegramOverlayBack } from '../../../hooks/useTelegramOverlayBack';
import { TG_OVERLAY_Z } from '../../../services/telegramBackStack';
import BfSheet from './BfSheet';
import { BfIconChat, BfIconLink } from './BfIcons';
import { DEFAULT_STUDY_TAB, STUDY_TABS, isStudyTab, studyTabLabel } from './studyTabs';
import { bfHaptic } from './BfHooks';
import { useHScroll } from './useHScroll';
import BfHScrollArrows from './BfHScrollArrows';

function isTgMobileFullscreen() {
  return (
    telegramService.isTelegramWebApp &&
    !telegramService.isTelegramWideLayout
  );
}

const API_BASE =
  import.meta.env.VITE_API_URL || 'https://verbum-api-production.up.railway.app/api';

const VERSE_SNIPPET_MAX = 96;
const SWIPE_MIN_DX = 64;

function clipVerseSnippet(text, maxLen = VERSE_SNIPPET_MAX) {
  const raw = (text ?? '').replace(/\s+/g, ' ').trim();
  if (!raw) return '';
  if (raw.length <= maxLen) return raw;
  return `${raw.slice(0, maxLen - 1).trim()}…`;
}

/**
 * BfAIInterpretation — разбор стиха по вкладкам (каждая — отдельный кэшируемый блок API).
 * Открывается сразу на нужной вкладке; вкладки листаются свайпом; внизу — «Спросить об этом стихе».
 */
export default function BfAIInterpretation({
  bookId,
  chapter,
  verse,
  translation = 'synodal',
  initialTab = DEFAULT_STUDY_TAB,
  onClose,
  onAsk,
  askPending = false,
}) {
  const navigate = useNavigate();
  const [interpretation, setInterpretation] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeMode, setActiveMode] = useState(() =>
    isStudyTab(initialTab) ? initialTab : DEFAULT_STUDY_TAB
  );
  const swipeRef = useRef(null);
  const { ref: tabsRef, edges: tabEdges, scrollStep: scrollTabs } = useHScroll();
  const [linkToast, setLinkToast] = useState(null);
  const [loadedBlocks, setLoadedBlocks] = useState(() => new Set());
  const [loadingBlock, setLoadingBlock] = useState(null);
  const [blockErrors, setBlockErrors] = useState({});
  const blockFetchRef = useRef(0);
  const abortRef = useRef(null);
  const scrollRef = useRef(null);
  const scrollTopByModeRef = useRef({});
  const [tgMobileFull, setTgMobileFull] = useState(isTgMobileFullscreen);

  useTelegramOverlayBack('aiInterpretation', true, onClose, TG_OVERLAY_Z.aiInterpretation);

  useEffect(() => {
    const syncLayout = () => setTgMobileFull(isTgMobileFullscreen());
    syncLayout();
    const root = document.documentElement;
    const mo = new MutationObserver(syncLayout);
    mo.observe(root, {
      attributes: true,
      attributeFilter: ['data-tg-desktop', 'data-tg-webapp', 'data-tg-fullscreen'],
    });
    window.addEventListener('resize', syncLayout);
    const tg = window.Telegram?.WebApp;
    const tgEvents = ['viewportChanged', 'fullscreenChanged', 'contentSafeAreaChanged'];
    tgEvents.forEach((ev) => {
      try {
        tg?.onEvent?.(ev, syncLayout);
      } catch {
        /* старые клиенты */
      }
    });
    return () => {
      mo.disconnect();
      window.removeEventListener('resize', syncLayout);
    };
  }, []);

  const loadBlock = async (block, { isInitial = false } = {}) => {
    if (loadedBlocks.has(block)) return;

    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    const fetchId = ++blockFetchRef.current;

    setLoadingBlock(block);
    if (isInitial) {
      setInitialLoading(true);
      setError(null);
    }
    setBlockErrors((prev) => {
      const next = { ...prev };
      delete next[block];
      return next;
    });

    try {
      const language = localStorage.getItem('bibleLanguage') || 'en';
      const res = await fetch(`${API_BASE}/interpret/block`, {
        method: 'POST',
        headers: jsonHeadersWithAuth({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          bookId: parseInt(bookId, 10),
          chapter: parseInt(chapter, 10),
          verse: parseInt(verse, 10),
          block,
          language,
          translation,
        }),
        signal: ac.signal,
      });
      if (res.status === 401) {
        const next = `${window.location.pathname}${window.location.search}`;
        requestAuthPopup(next, true);
        throw new Error('__AUTH_REQUIRED__');
      }
      if (res.status === 429) {
        const payload = await res.json().catch(() => null);
        if (payload?.code === 'DAILY_AI_LIMIT_REACHED') {
          throw new Error(resolveAiLimitMessage(payload, t, 'interpretation'));
        }
      }
      if (!res.ok) throw new Error(t('error'));
      const data = await res.json();
      if (fetchId !== blockFetchRef.current) return;

      setInterpretation((prev) => ({ ...(prev || {}), ...data }));
      setLoadedBlocks((prev) => new Set(prev).add(block));
    } catch (err) {
      if (err?.name === 'AbortError') return;
      if (fetchId !== blockFetchRef.current) return;
      const message = err?.message === '__AUTH_REQUIRED__' ? t('error') : err.message || t('error');
      if (isInitial) setError(message);
      setBlockErrors((prev) => ({ ...prev, [block]: message }));
    } finally {
      if (fetchId === blockFetchRef.current) {
        setLoadingBlock(null);
        if (isInitial) setInitialLoading(false);
        if (abortRef.current === ac) abortRef.current = null;
      }
    }
  };

  useEffect(() => {
    setInterpretation(null);
    setError(null);
    setInitialLoading(true);
    setLoadedBlocks(new Set());
    setBlockErrors({});
    scrollTopByModeRef.current = {};
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [bookId, chapter, verse, translation]);

  // Один источник загрузки: активная вкладка. Ошибка не перезапрашивается сама — только «Повторить».
  useEffect(() => {
    if (loadedBlocks.has(activeMode) || loadingBlock === activeMode || blockErrors[activeMode]) return;
    loadBlock(activeMode, { isInitial: !interpretation });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMode, loadedBlocks, loadingBlock, blockErrors, interpretation]);

  useEffect(() => {
    document
      .getElementById(`bf-ai-tab-${activeMode}`)
      ?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [activeMode, interpretation]);

  const handleCopyLink = async () => {
    const isTelegram = telegramService.isTelegramWebApp;
    const link = isTelegram
      ? `https://t.me/VerbumBibleBot/app?startapp=${bookId}_${chapter}_${verse}_interpretation`
      : `${window.location.origin}/synodal/${bookId}/${chapter}/${verse}/interpretation`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        setLinkToast(t('linkCopied'));
        setTimeout(() => setLinkToast(null), 1800);
      } else if (isTelegram) {
        telegramService.showAlert(`Link:\n${link}\n\n${t('copyManually')}`);
      }
    } catch {
      /* silent */
    }
  };

  const renderCrossRef = (ref, idx) => {
    if (typeof ref === 'object' && ref?.bookId) {
      const url = `/synodal/${ref.bookId}/${ref.chapter}${ref.verse ? `/${ref.verse}` : ''}`;
      return (
        <button
          key={idx}
          type="button"
          className="bf-xref"
          onClick={() => {
            onClose?.();
            navigate(url);
          }}
        >
          {ref.text}
        </button>
      );
    }
    return (
      <span key={idx} className="bf-xref">
        {ref}
      </span>
    );
  };

  const selectMode = (modeId) => {
    if (modeId === activeMode) return;
    if (scrollRef.current) {
      scrollTopByModeRef.current[activeMode] = scrollRef.current.scrollTop;
    }
    setActiveMode(modeId);
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (!el) return;
      const saved = scrollTopByModeRef.current[modeId];
      el.scrollTop = typeof saved === 'number' ? saved : 0;
      requestAnimationFrame(() => {
        const max = Math.max(0, el.scrollHeight - el.clientHeight);
        if (el.scrollTop > max) el.scrollTop = max;
      });
    });
  };

  const onSwipeStart = (e) => {
    const touch = e.touches?.[0];
    swipeRef.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
  };
  const onSwipeEnd = (e) => {
    const start = swipeRef.current;
    swipeRef.current = null;
    const touch = e.changedTouches?.[0];
    if (!start || !touch) return;
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    if (Math.abs(dx) < SWIPE_MIN_DX || Math.abs(dx) < Math.abs(dy) * 1.6) return;
    const index = STUDY_TABS.findIndex((tab) => tab.id === activeMode);
    const next = STUDY_TABS[index + (dx < 0 ? 1 : -1)];
    if (!next) return;
    bfHaptic('light');
    selectMode(next.id);
  };

  const tabChip = (tab) => (
    <button
      key={tab.id}
      id={`bf-ai-tab-${tab.id}`}
      type="button"
      role="tab"
      aria-selected={activeMode === tab.id}
      aria-controls={`bf-ai-panel-${tab.id}`}
      className={`bf-chip bf-ai-tabs__chip${tab.primary ? '' : ' bf-ai-tabs__chip--minor'}`}
      data-active={activeMode === tab.id ? 'true' : 'false'}
      onClick={() => selectMode(tab.id)}
    >
      {studyTabLabel(tab)}
    </button>
  );

  const sheetClassName = tgMobileFull ? 'bf-sheet--ai-tg' : '';
  const backdropClassName = tgMobileFull ? 'bf-sheet-backdrop--ai-tg' : '';
  const bodyClassName = tgMobileFull ? 'bf-sheet__body--ai-full' : 'bf-sheet__body--ai-sheet';

  return (
    <BfSheet
      sheetClassName={sheetClassName}
      backdropClassName={backdropClassName}
      bodyClassName={bodyClassName}
      title={t('interpretation')}
      titleMark
      onClose={onClose}
      ariaLabel={t('interpretation')}
      rightActions={
        <button
          type="button"
          className="bf-icon-btn"
          onClick={handleCopyLink}
          aria-label={t('copyLink')}
          title={t('copyLink')}
        >
          <BfIconLink />
        </button>
      }
    >
      {initialLoading && !interpretation && (
        <div className="bf-loading">
          <div className="bf-ai-dot" />
          <div>{t('analyzingScripture')}</div>
          <div className="bf-loading__hint">{t('mayTakeMinutes')}</div>
        </div>
      )}

      {error && !interpretation && (
        <div className="bf-error">
          <p className="bf-error__title">{t('interpretationLoadSoftErrorTitle')}</p>
          <p className="bf-error__body">{error}</p>
          <button
            type="button"
            className="bf-pill-cta"
            onClick={() => loadBlock(activeMode, { isInitial: true })}
          >
            {t('retry')}
          </button>
        </div>
      )}

      {interpretation && (
        <div className="bf-ai-layout">
          <div className="bf-ai-layout__head">
            <div
              className="bf-ai-ref bf-ai-ref--compact"
              title={interpretation.text ? `«${interpretation.text}»` : undefined}
            >
              <span className="bf-ai-ref__label">{interpretation.reference}</span>
              <span className="bf-ai-ref__quote">
                {interpretation.text ? `«${clipVerseSnippet(interpretation.text)}»` : ''}
              </span>
            </div>

            <div className="bf-hscroll">
              <div
                ref={tabsRef}
                className="bf-ai-tabs"
                role="tablist"
                aria-label={t('interpretation')}
              >
                {STUDY_TABS.filter((tab) => tab.primary).map(tabChip)}
                <span className="bf-ai-tabs__sep" aria-hidden="true" />
                {STUDY_TABS.filter((tab) => !tab.primary).map(tabChip)}
              </div>
              <BfHScrollArrows edges={tabEdges} onStep={scrollTabs} />
            </div>
          </div>

          <div
            className="bf-ai-layout__scroll"
            ref={scrollRef}
            onTouchStart={onSwipeStart}
            onTouchEnd={onSwipeEnd}
          >
            {STUDY_TABS.map((tab) => (
              <div
                key={tab.id}
                id={`bf-ai-panel-${tab.id}`}
                className="bf-ai-tab-panel"
                role="tabpanel"
                aria-labelledby={`bf-ai-tab-${tab.id}`}
                hidden={activeMode !== tab.id}
              >
                <TabPanelBody
                  mode={tab.id}
                  data={interpretation}
                  loading={loadingBlock === tab.id || !loadedBlocks.has(tab.id)}
                  blockError={blockErrors[tab.id]}
                  onRetry={() => loadBlock(tab.id)}
                  renderCrossRef={renderCrossRef}
                />
              </div>
            ))}
          </div>

          {onAsk && (
            <button
              type="button"
              className="bf-ai-ask"
              onClick={onAsk}
              data-pending={askPending ? 'true' : undefined}
              aria-busy={askPending ? 'true' : undefined}
            >
              <BfIconChat />
              <span>{t('studyAsk')}</span>
            </button>
          )}
        </div>
      )}

      {linkToast && <div className="bf-ai-toast">{linkToast}</div>}
    </BfSheet>
  );
}

function TabPanelBody({ mode, data, loading, blockError, onRetry, renderCrossRef }) {
  if (loading && !blockError) {
    return (
      <div className="bf-loading bf-loading--inline">
        <div className="bf-ai-dot" />
        <div>{t('analyzingScripture')}</div>
      </div>
    );
  }
  if (blockError) {
    return (
      <div className="bf-error bf-error--inline">
        <p className="bf-error__body">{blockError}</p>
        <button type="button" className="bf-pill-cta" onClick={onRetry}>
          {t('retry')}
        </button>
      </div>
    );
  }
  return <ModeContent mode={mode} data={data} renderCrossRef={renderCrossRef} />;
}

function SectionBlock({ label, children, className = '' }) {
  return (
    <section className={['bf-ai-block', className].filter(Boolean).join(' ')}>
      {label && <h3 className="bf-ai-block__title">{label}</h3>}
      {children}
    </section>
  );
}

function ModeContent({ mode, data, renderCrossRef }) {
  switch (mode) {
    case 'meaning':
      return (
        <div className="bf-ai-pane">
          {data.meaning?.summary && (
            <p className="bf-ai-summary">{data.meaning.summary}</p>
          )}
          {data.definitions?.length > 0 && (
            <SectionBlock label={t('terms')} className="bf-ai-block--terms">
              <div className="bf-def-stack bf-def-stack--compact">
                {data.definitions.map((d, i) => (
                  <div key={i} className="bf-def-item">
                    <div className="bf-def-term">{d.term}</div>
                    <div className="bf-def-body">{d.definition}</div>
                  </div>
                ))}
              </div>
            </SectionBlock>
          )}
          {data.crossReferences?.length > 0 && (
            <SectionBlock label={t('crossReferences')}>
              <div className="bf-xref-row">
                {data.crossReferences.map((r, i) => renderCrossRef(r, i))}
              </div>
            </SectionBlock>
          )}
        </div>
      );

    case 'simple': {
      const simple = data?.simple && typeof data.simple === 'object' ? data.simple : null;
      if (!simple?.image && !simple?.keyIdea) {
        return (
          <div className="bf-ai-pane">
            <p className="bf-ai-text">{t('noSimple')}</p>
          </div>
        );
      }
      return (
        <div className="bf-ai-pane">
          {simple.keyIdea && (
            <div className="bf-ai-key">
              <span className="bf-ai-key__label">{t('studyKeyIdea')}</span>
              <p className="bf-ai-key__text">{simple.keyIdea}</p>
            </div>
          )}
          {simple.image && <p className="bf-ai-summary bf-ai-simple">{simple.image}</p>}
        </div>
      );
    }

    case 'apply': {
      const app = data?.application;
      const principle =
        app && typeof app === 'object' ? app.principle ?? app.Principle ?? '' : '';
      const modernContext =
        app && typeof app === 'object'
          ? app.modernContext ?? app.modern_context ?? app.ModernContext ?? ''
          : '';
      return (
        <div className="bf-ai-pane">
          {!app && <p className="bf-ai-text">{t('noData')}</p>}
          {typeof app === 'string' && <p className="bf-ai-text">{app}</p>}
          {typeof app === 'object' && (
            <>
              {principle && (
                <SectionBlock label={t('principle')}>
                  <p className="bf-ai-text">{principle}</p>
                </SectionBlock>
              )}
              {modernContext && (
                <SectionBlock label={t('today')}>
                  <p className="bf-ai-text">{modernContext}</p>
                </SectionBlock>
              )}
              {!principle && !modernContext && (
                <p className="bf-ai-text">{t('noApplicationYet')}</p>
              )}
            </>
          )}
        </div>
      );
    }

    case 'context':
      return (
        <div className="bf-ai-pane">
          {data.context?.immediate && (
            <SectionBlock label={t('immediateContext')}>
              <p className="bf-ai-text">{data.context.immediate}</p>
            </SectionBlock>
          )}
          {data.context?.bookContext && (
            <SectionBlock label={t('bookContext')}>
              <p className="bf-ai-text">{data.context.bookContext}</p>
            </SectionBlock>
          )}
          {data.context?.biblicalContext && (
            <SectionBlock label={t('biblicalContext')}>
              <p className="bf-ai-text">{data.context.biblicalContext}</p>
            </SectionBlock>
          )}
          {!data.context && <p className="bf-ai-text">{t('noData')}</p>}
        </div>
      );

    case 'theological':
      return (
        <div className="bf-ai-pane">
          {data.theological?.themes?.length > 0 && (
            <SectionBlock label={t('theology')}>
              <div className="bf-tags">
                {data.theological.themes.map((th, i) => (
                  <span key={i} className="bf-tag">
                    {th}
                  </span>
                ))}
              </div>
            </SectionBlock>
          )}
          {data.theological?.christConnection && (
            <SectionBlock label={t('christConnection')}>
              <p className="bf-ai-text">{data.theological.christConnection}</p>
            </SectionBlock>
          )}
          {data.theological?.typology && (
            <SectionBlock label={t('typology')}>
              <p className="bf-ai-text">{data.theological.typology}</p>
            </SectionBlock>
          )}
          {data.crossReferences?.length > 0 && (
            <SectionBlock label={t('crossReferences')}>
              <div className="bf-xref-row">
                {data.crossReferences.map((r, i) => renderCrossRef(r, i))}
              </div>
            </SectionBlock>
          )}
          {!data.theological && <p className="bf-ai-text">{t('noData')}</p>}
        </div>
      );

    case 'original':
      return (
        <div className="bf-ai-pane">
          {data.original?.keyWords?.length > 0 ? (
            <>
              <SectionBlock label={`${t('originalTab')}: ${data.original.language}`}>
                <div className="bf-kw-stack bf-kw-stack--compact">
                  {data.original.keyWords.map((w, i) => (
                    <div key={i} className="bf-kw bf-kw--compact">
                      <div className="bf-kw__original">{w.original}</div>
                      <div className="bf-kw__translit">{w.transliteration}</div>
                      <div className="bf-kw__meaning">{w.meaning}</div>
                    </div>
                  ))}
                </div>
              </SectionBlock>
              {data.original.notes && (
                <p className="bf-ai-text bf-ai-text--note">{data.original.notes}</p>
              )}
            </>
          ) : (
            <p className="bf-ai-text">{t('noOriginal')}</p>
          )}
        </div>
      );

    case 'culture':
      return (
        <div className="bf-ai-pane">
          {data.cultural?.facts?.length > 0 && (
            <SectionBlock label={t('culture')}>
              <ul className="bf-ai-list">
                {data.cultural.facts.map((f, i) => (
                  <li key={i} className="bf-ai-text">
                    {f}
                  </li>
                ))}
              </ul>
            </SectionBlock>
          )}
          {data.historical?.facts?.length > 0 && (
            <SectionBlock label={t('historical')}>
              <ul className="bf-ai-list">
                {data.historical.facts.map((f, i) => (
                  <li key={i} className="bf-ai-text">
                    {f}
                  </li>
                ))}
              </ul>
            </SectionBlock>
          )}
          {!data.cultural?.facts?.length && !data.historical?.facts?.length && (
            <p className="bf-ai-text">{t('noCultureHistory')}</p>
          )}
        </div>
      );

    case 'faq': {
      const list = Array.isArray(data.faq)
        ? data.faq
        : Array.isArray(data.FAQ)
          ? data.FAQ
          : [];
      return (
        <div className="bf-ai-pane">
          {list.length > 0 ? (
            <div className="bf-def-stack bf-def-stack--compact">
              {list.map((q, i) => (
                <div key={i} className="bf-def-item">
                  <div className="bf-def-term">{q.question ?? q.Question}</div>
                  <div className="bf-def-body">{q.answer ?? q.Answer}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="bf-ai-text">{t('noFaq')}</p>
          )}
        </div>
      );
    }

    default:
      return null;
  }
}
